import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { buildSeo } from "@/lib/seo";
import { Markdown } from "@/components/TwinChat";
import { MarketingLayout, Eyebrow } from "@/components/marketing/Layout";
import { runTwinCheck, getTwinCheck, askDemoTwin } from "@/lib/twin-check.functions";
import type { TwinCheckReport, CheckItem } from "@/lib/twin-check.server";
import {
  ArrowRight,
  Check,
  AlertTriangle,
  X,
  Loader2,
  Search,
  Sparkles,
  Share2,
  Gauge,
  MessageSquare,
  Send,
} from "lucide-react";

export const Route = createFileRoute("/twin-check")({
  validateSearch: (s: Record<string, unknown>): { r?: string; url?: string } => ({
    r: typeof s.r === "string" && s.r ? s.r : undefined,
    // ?url= lets the homepage hand a website straight into the check, so the
    // aha-moment starts on the landing page and completes here.
    url: typeof s.url === "string" && s.url ? s.url : undefined,
  }),
  loaderDeps: ({ search }) => ({ r: search.r }),
  loader: async ({ deps }) => {
    if (!deps.r) return { report: null as TwinCheckReport | null };
    const res = await getTwinCheck({ data: { slug: deps.r } });
    return { report: res.ok ? res.report : null };
  },
  head: ({ loaderData }) => {
    const rep = loaderData?.report;
    if (rep) {
      return buildSeo({
        path: "/twin-check",
        title: `${rep.hostname}: AI-readability ${rep.score}/100 | Twin-Check`,
        // Kept short enough that the hostname and score survive truncation —
        // they are the part that earns the click.
        description: `AI-visibility audit of ${rep.hostname}: how well AI models and agents read and trust this company. Score ${rep.score}/100, with concrete fixes.`,
      });
    }
    return buildSeo({
      path: "/twin-check",
      title: "Twin-Check — is your company readable by AI? Free audit",
      description:
        "Free check: paste your website and see how AI models and agents read, understand and trust your company. Get a score, concrete findings and how to fix them.",
    });
  },
  component: TwinCheckPage,
});

const STATUS_META: Record<CheckItem["status"], { icon: typeof Check; cls: string; ring: string }> =
  {
    pass: { icon: Check, cls: "text-emerald-600", ring: "bg-emerald-500/10 border-emerald-500/30" },
    warn: {
      icon: AlertTriangle,
      cls: "text-amber-600",
      ring: "bg-amber-500/10 border-amber-500/30",
    },
    fail: { icon: X, cls: "text-red-600", ring: "bg-red-500/10 border-red-500/30" },
  };

function gradeColor(score: number): string {
  if (score >= 85) return "oklch(0.72 0.17 150)";
  if (score >= 70) return "oklch(0.78 0.15 120)";
  if (score >= 55) return "oklch(0.82 0.15 90)";
  if (score >= 40) return "oklch(0.8 0.16 60)";
  return "oklch(0.72 0.19 25)";
}

function ScoreDial({ score, grade }: { score: number; grade: string }) {
  const r = 52;
  const c = 2 * Math.PI * r;
  const color = gradeColor(score);
  return (
    <div className="relative h-32 w-32 shrink-0">
      <svg viewBox="0 0 120 120" className="h-32 w-32 -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="10" />
        <circle
          cx="60"
          cy="60"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c - (c * score) / 100}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-serif text-[40px] leading-none" style={{ color }}>
          {score}
        </div>
        <div className="text-[11px] text-muted-foreground">Grade {grade}</div>
      </div>
    </div>
  );
}

function DemoTwinChat({ url, hostname }: { url: string; hostname: string }) {
  const ask = useServerFn(askDemoTwin);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [msgs, setMsgs] = useState<{ role: "user" | "twin"; text: string }[]>([]);

  async function run(question: string) {
    if (!question.trim() || busy) return;
    setBusy(true);
    setFailed(false);
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setQ("");
    try {
      const r = await ask({ data: { url, question } });
      if (r.ok && r.answer) setMsgs((m) => [...m, { role: "twin", text: r.answer }]);
      else setFailed(true);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  const suggestions = ["What do you offer?", "Who is this for?", "How do I get in touch?"];

  return (
    <div className="hairline rounded-2xl bg-card p-6 sm:p-7">
      <div className="flex items-center gap-1.5 font-mono-eyebrow">
        <MessageSquare className="h-3.5 w-3.5" /> See what a Twin would answer
      </div>
      <h3 className="mt-2 font-serif text-[22px] leading-tight tracking-[-0.02em]">
        Ask {hostname}’s Twin — live, grounded on its own site.
      </h3>
      <p className="mt-1.5 text-[13.5px] text-muted-foreground">
        Built in seconds from the page above. No account. This is the fix, running on your data.
      </p>

      {msgs.length > 0 && (
        <div className="mt-5 space-y-3">
          {msgs.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed ${
                  m.role === "user"
                    ? "bg-foreground text-background whitespace-pre-wrap"
                    : "hairline bg-cream text-foreground/90"
                }`}
              >
                {/* Twin replies are markdown — render them, don't print the
                    raw ** around bold text. */}
                {m.role === "twin" ? <Markdown text={m.text} sources={[]} /> : m.text}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="hairline bg-cream rounded-2xl px-4 py-2.5">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
        </div>
      )}

      {msgs.length === 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {suggestions.map((s) => (
            <button
              key={s}
              onClick={() => run(s)}
              disabled={busy}
              className="rounded-full hairline bg-background px-3 py-1.5 text-[12.5px] text-muted-foreground hover:text-foreground hover:bg-cream transition disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {failed && (
        <p className="mt-3 text-[13px] text-muted-foreground">
          The demo Twin couldn’t answer that right now — try another question.
        </p>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          run(q);
        }}
        className="mt-4 flex gap-2"
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Ask ${hostname}'s Twin anything…`}
          className="flex-1 rounded-full hairline bg-background px-4 py-2.5 text-[14px] focus:outline-none focus:border-foreground/30"
        />
        <button
          type="submit"
          aria-label="Send message"
          disabled={busy || !q.trim()}
          className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2.5 text-[13.5px] font-medium text-background hover:opacity-90 disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" />
        </button>
      </form>
    </div>
  );
}

function ReportView({ report, shareUrl }: { report: TwinCheckReport; shareUrl?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-8">
      <div className="hairline rounded-2xl bg-card p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <ScoreDial score={report.score} grade={report.grade} />
          <div className="min-w-0 flex-1">
            <Eyebrow>AI-readability report</Eyebrow>
            <h2 className="mt-2 font-serif text-[28px] leading-tight tracking-[-0.02em] break-words">
              {report.hostname}
            </h2>
            {report.summary && (
              <div className="mt-4 rounded-xl hairline bg-cream p-4">
                <div className="flex items-center gap-1.5 font-mono-eyebrow mb-2">
                  <Sparkles className="h-3.5 w-3.5" /> How an AI model reads you
                </div>
                <p className="text-[14.5px] leading-relaxed text-foreground/85 whitespace-pre-wrap">
                  {report.summary}
                </p>
              </div>
            )}
            {shareUrl && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                  });
                }}
                className="mt-4 inline-flex items-center gap-1.5 rounded-full hairline bg-background px-3.5 py-1.5 text-[13px] hover:bg-cream transition"
              >
                <Share2 className="h-3.5 w-3.5" /> {copied ? "Link copied" : "Copy shareable link"}
              </button>
            )}
          </div>
        </div>
      </div>

      <DemoTwinChat url={report.url} hostname={report.hostname} />

      <div>
        <div className="font-mono-eyebrow mb-3">What we checked</div>
        <div className="hairline rounded-2xl overflow-hidden divide-y divide-border">
          {report.checks.map((c) => {
            const m = STATUS_META[c.status];
            const Icon = m.icon;
            return (
              <div key={c.id} className="flex items-start gap-3 bg-card p-4">
                <span
                  className={`mt-0.5 h-6 w-6 shrink-0 rounded-full border flex items-center justify-center ${m.ring}`}
                >
                  <Icon className={`h-3.5 w-3.5 ${m.cls}`} strokeWidth={2.5} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[14.5px] font-medium">{c.label}</div>
                  <div className="text-[13px] text-muted-foreground mt-0.5">{c.detail}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* The fix is the product */}
      <div className="hairline rounded-2xl bg-cream p-6 sm:p-8">
        <div className="font-mono-eyebrow">The finding is free. The fix is a Twin.</div>
        <h3 className="mt-3 font-serif text-[26px] leading-tight tracking-[-0.02em] max-w-2xl">
          Install a Reality Twin and your company becomes machine-answerable — grounded, verified
          and reachable in ChatGPT & Claude.
        </h3>
        <div className="mt-6 flex flex-wrap gap-2">
          <Link
            to="/auth"
            className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-[13.5px] font-medium text-background hover:opacity-90"
          >
            Build your Twin — free <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
          <Link
            to="/platform"
            className="inline-flex items-center rounded-full hairline bg-background px-4 py-2 text-[13.5px]"
          >
            How it works
          </Link>
        </div>
      </div>
    </div>
  );
}

function TwinCheckPage() {
  const { report: loaded } = Route.useLoaderData();
  const { url: incomingUrl } = Route.useSearch();
  const navigate = useNavigate();
  const check = useServerFn(runTwinCheck);
  const [url, setUrl] = useState(incomingUrl ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inline, setInline] = useState<TwinCheckReport | null>(null);

  // Arrived from the homepage field with a website already typed: run it
  // immediately so the visitor never has to ask twice.
  useEffect(() => {
    if (incomingUrl && !loaded) void run(incomingUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    await run(url);
  }

  async function run(raw: string) {
    const v = raw.trim();
    if (!v || busy) return;
    setBusy(true);
    setError(null);
    setInline(null);
    try {
      const res = await check({ data: { url: v } });
      if (!res.ok) {
        setError("We couldn't reach that site. Check the address and try again.");
      } else if (res.slug) {
        navigate({ to: "/twin-check", search: { r: res.slug } });
      } else {
        setInline(res.report);
      }
    } catch {
      setError("Something went wrong running the check. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  const shareUrl =
    typeof window !== "undefined" ? window.location.href : "https://realitytwin.io/twin-check";

  return (
    <MarketingLayout>
      <section className="border-b border-border">
        <div className="mx-auto max-w-3xl px-6 pt-20 pb-14 sm:pt-28">
          <Eyebrow>
            <span className="inline-flex items-center gap-1.5">
              <Gauge className="h-3.5 w-3.5" /> Twin-Check · free
            </span>
          </Eyebrow>
          <h1 className="mt-4 font-serif text-[42px] sm:text-[60px] leading-[1.0] tracking-[-0.035em]">
            Can AI read your company?
          </h1>
          <p className="mt-5 text-[17px] leading-[1.6] text-muted-foreground max-w-xl">
            Two of three searches now end without a click — the answer replaces the visit. Paste
            your website and see exactly how AI models and agents read, understand and trust you. No
            account.
          </p>

          <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-2 max-w-xl">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="yourcompany.com"
                className="w-full rounded-full hairline bg-card pl-10 pr-4 py-3 text-[15px] focus:outline-none focus:border-foreground/30"
              />
            </div>
            <button
              type="submit"
              disabled={busy || !url.trim()}
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90 disabled:opacity-50"
            >
              {busy ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Checking…
                </>
              ) : (
                <>
                  Run free check <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </>
              )}
            </button>
          </form>
          {error && <p className="mt-3 text-[13.5px] text-red-600">{error}</p>}
          {busy && (
            <p className="mt-3 text-[13px] text-muted-foreground">
              Fetching your site the way an AI agent would…
            </p>
          )}
        </div>
      </section>

      <section className="py-14 sm:py-16">
        <div className="mx-auto max-w-3xl px-6">
          {loaded ? (
            <ReportView report={loaded} shareUrl={shareUrl} />
          ) : inline ? (
            <ReportView report={inline} />
          ) : (
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                [
                  "Readable without JS",
                  "Agents read your server HTML, not your app. Empty shells are invisible.",
                ],
                [
                  "Verifiable identity",
                  "Structured data lets models resolve who and what you are.",
                ],
                [
                  "AI crawlers allowed",
                  "If robots.txt blocks GPTBot & co., those models can't index you.",
                ],
              ].map(([h, b]) => (
                <div key={h} className="hairline rounded-xl bg-card p-5">
                  <div className="font-medium text-[14.5px]">{h}</div>
                  <div className="mt-1.5 text-[13px] text-muted-foreground leading-relaxed">
                    {b}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}
